import { GetString } from "fluent-react";
import { debounce } from "lodash-es";
import random from "lodash-es/random";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { CountryProfilesLocalizationAndBundleContext } from "../contextProviders/getFluentLocalizationContexts";
import { LookBackYearRange, QueryLevel } from "../graphQL/types/shared";
import {
  Location,
  PolicyRecommendation,
  ProductClass,
  TradeDirection,
  TradeFlow,
} from "../graphQL/types/shared";
import {
  extractFromStringLocationId,
  generateStringLocationId,
} from "../graphQL/Utils";
import {
  useLink,
  usePushStateByDescriptor,
  useReplaceStateByDescriptor,
} from "../routing";
import {
  countryProfilesCountryParamName,
  countryProfilesSlideParamName,
  RouteID,
} from "../routing/routes";
import usePresentationStepperBehavior from "./usePresentationStepperBehavior";

import styled from "styled-components";
import { gql, useQuery } from "../graphQL/useQuery";
import NewOverlayContainer from "../sharedComponents/NewOverlayContainer";
import {
  latestYear,
  overlayPortalContainerId,
  Target,
  xIconHTMLEntity,
} from "../Utils";
import { numericDefaultCountryPool, QueryStore } from "../viz/queryStoreUtils";
import { convertQueryStoreIntoQueryParams } from "../viz/routingUtils";
import { NodeSizing, VizType, vizTypeToRouteID } from "../viz/Utils";
import getConclusionSlide from "./conclusion";
import getCountryProductSpace from "./countryProductSpace";
import getDiversificationPaths from "./diversificationPaths";
import getExportBasketSlide from "./exportBasket";
import getExportComplexity from "./exportComplexity";
import getExportGrowthDynamics from "./exportGrowthDynamics";
import getGrowthGlobalMarketShare from "./growthGlobalMarketShare";
import getGrowthOpportunities from "./growthOpportunities";
import getHighPotentialProducts from "./highPotentialProducts";
import getLandingSlide from "./landing";
import Localized from "./landing/Localized";
import getNewProducts from "./newProducts";
import getProductSpace from "./productSpace";
import usePromotableZIndex from "./usePromotableZIndex";
import {
  CommonElementName,
  ExportsChangeNodeSizing,
  PolicyDimension,
  SlideName,
  slideSlugInfo,
  TreeMapColorCriterion,
} from "./Utils";
import { primaryColor2 } from "./Utils";

const query = gql`
  query isInCp($id: ID!) {
    location(id: $id) {
      id
      isInCountryPages
      shortName
    }
  }
`;

interface SuccessResponse {
  location: {
    id: Location["id"];
    isInCountryPages: Location["isInCountryPages"];
    shortName: Location["shortName"];
  };
}

interface Variables {
  id: string;
}

const zIndices = {
  landingContent: 1000,
  conclusionContent: 1000,
  [CommonElementName.HamburgerMenu]: 990,
  [CommonElementName.HelpTipsTrigger]: 980,
  landingBackground: 980,
  conclusionBackground: 980,
  countrySelector: 970,
  sectionNav: 960,
  [CommonElementName.Sidebar]: 940,
  [CommonElementName.LeftPanelBackground]: 930,
  sectorLabels: 930,
  [CommonElementName.ExportBasketExportComplexityTreeMap]: 910,
  newProductsChart: 910,
  exportGrowthDynamicsGraph: 900,
  [CommonElementName.ExportBasketExportComplexityTradeFlowSelector]: 890,
  [CommonElementName.ExportBasketExportComplexityColorSelector]: 880,
  exportComplexityTreeMapLegend: 860,
  exportGrowthDynamicsYearRangeSelector: 850,
  exportGrowthDynamicsSizeSelector: 840,
  exportGrowthDynamicsLegend: 830,
  rockSongFlowChartZIndex: 830,
  exportGrowthDynamicsTopOfPageDropShadow: 820,
  rockSongChart: 810,
  feasibilityChart: 800,
  newProductsLegend: 790,
  topProductsTable: 790,
  feasibilyControls: 770,
  marketShareChart: 760,
  ExploreEngageButtons: 740,
  topPageIndicators: 720,
  genericProductSpaceLabels: 710,
  genericProductSpace: 700,
};

const promotedZIndex = 3000; // Z-index used to make an element appear above sibling elements
const overlayZIndex = 3010; // Tooltips should appear above all promoted elements
export const hamburgerMenuOpenZIndex = 3020; // Open menu should always appear on top of everything

interface Props {
  pathParams: Record<string, string | undefined>;
}

const slideIndexToSlug = (index: number) => {
  const found = Object.entries(slideSlugInfo).find(
    ([, info]) => info.index === index
  );
  if (found === undefined) {
    throw new Error("Invalid slide index" + index);
  }
  const [slideName, { slug: foundSlug }] = found;
  const slugToSendOut = slideName === SlideName.Landing ? undefined : foundSlug;
  return slugToSendOut;
};

const slugToSlideIndex = (rawSlideName: string | undefined): number => {
  let slideIndex: number;
  if (rawSlideName === undefined) {
    slideIndex = slideSlugInfo[SlideName.Landing].index;
  } else if (
    rawSlideName === slideSlugInfo[SlideName.ExportBasket].slug ||
    rawSlideName === slideSlugInfo[SlideName.ExportComplexity].slug ||
    rawSlideName === slideSlugInfo[SlideName.ExportGrowthDynamics].slug ||
    rawSlideName === slideSlugInfo[SlideName.DiversificationPaths].slug ||
    rawSlideName === slideSlugInfo[SlideName.GrowthOpportunities].slug ||
    rawSlideName === slideSlugInfo[SlideName.HighPotentialProducts].slug ||
    rawSlideName === slideSlugInfo[SlideName.GrowthGlobalMarketShare].slug ||
    rawSlideName === slideSlugInfo[SlideName.NewProducts].slug ||
    rawSlideName === slideSlugInfo[SlideName.ProductSpace].slug ||
    rawSlideName === slideSlugInfo[SlideName.CountryProductSpace].slug ||
    rawSlideName === slideSlugInfo[SlideName.Conclusion].slug
  ) {
    const found = Object.entries(slideSlugInfo).find(
      ([, info]) => info.slug === rawSlideName
    )!;
    [, { index: slideIndex }] = found;
  } else {
    slideIndex = slideSlugInfo[SlideName.Landing].index;
  }

  return slideIndex;
};

const OverlayScreenMessageContainer = styled.div`
  background-color: #1b1f2f;
  opacity: 0.9;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  font-size: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 0 0 20vw;
`;
const OverlayScreenMessage = styled.div`
  color: white;
  font-size: 1rem;
  width: 60vw;
  margin-top: 1rem;
`;
const OverlayScreenLogo = styled.img`
  top: 0;
  height: 40px;
  min-height: 45px;
`;
const OverlayScreenMessageCloseButton = styled.div`
  position: absolute;
  right: 5vw;
  top: 5vw;
  width: 50px;
  height: 50px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
`;
const smallScreenWidthThreshold = 700; // in px
const smallScreenHeightThreshold = 550; // in px

const smallScreenMediaQueryString = `(max-height: ${smallScreenHeightThreshold}px), (max-width: ${smallScreenWidthThreshold}px)`;

const OverlayLink = styled.a`
  color: ${primaryColor2};

  &:hover {
    text-decoration: underline;
  }
`;

const CountryProfiles = (props: Props) => {
  const { pathParams } = props;
  const rawCountry = pathParams[countryProfilesCountryParamName];
  const rawSlideName = pathParams[countryProfilesSlideParamName];
  const historyReplaceState = useReplaceStateByDescriptor();
  const historyPushState = usePushStateByDescriptor();

  // parse country ID from URL:
  let numericCountryId: number;
  if (rawCountry === undefined) {
    numericCountryId =
      numericDefaultCountryPool[random(numericDefaultCountryPool.length - 1)];
    historyReplaceState({
      id: RouteID.CountryProfiles,
      pathParams: {
        [countryProfilesCountryParamName]: numericCountryId.toString(),
      },
    });
  } else {
    const parsedNumericId = parseInt(rawCountry, 10);
    if (Number.isNaN(parsedNumericId)) {
      numericCountryId =
        numericDefaultCountryPool[random(numericDefaultCountryPool.length - 1)];
      historyReplaceState({
        id: RouteID.CountryProfiles,
        pathParams: {
          [countryProfilesCountryParamName]: numericCountryId.toString(),
        },
      });
    } else {
      numericCountryId = parsedNumericId;
    }
  }

  const locationId = generateStringLocationId(numericCountryId);

  const slideIndex = slugToSlideIndex(rawSlideName);

  const setSlideIndex = (updater: (value: number) => number) => {
    const newIndex = updater(slideIndex);
    historyPushState({
      id: RouteID.CountryProfiles,
      pathParams: {
        [countryProfilesCountryParamName]: extractFromStringLocationId(
          locationId
        ).toString(),
        [countryProfilesSlideParamName]: slideIndexToSlug(newIndex),
      },
    });
  };

  const { getZIndex, promoteZIndex, restoreZIndex } = usePromotableZIndex({
    normalZIndices: zIndices,
    promotedZIndex,
  });

  const getTreeMapTradeFlowDropodwnZIndex = () =>
    getZIndex(CommonElementName.ExportBasketExportComplexityTradeFlowSelector);

  const sectorLabelsZIndex = getZIndex("sectorLabels");
  const hamburgerMenuZIndex = getZIndex(CommonElementName.HamburgerMenu);
  const helpTipsTriggerZIndex = getZIndex(CommonElementName.HelpTipsTrigger);
  const sidebarZIndex = getZIndex(CommonElementName.Sidebar);
  const interiorCountrySelectorZIndex = getZIndex("countrySelector");
  const exportBasketExportComplexityTreeMapZIndex = getZIndex(
    CommonElementName.ExportBasketExportComplexityTreeMap
  );
  const sectionNavZIndex = getZIndex("sectionNav");
  const exploreEngageButtonsZIndex = getZIndex("ExploreEngageButtons");

  const setCountry = (id: string) => {
    const numericLocationId = extractFromStringLocationId(id);
    historyPushState({
      id: RouteID.CountryProfiles,
      pathParams: {
        [countryProfilesCountryParamName]: numericLocationId.toString(),
        [countryProfilesSlideParamName]: slideIndexToSlug(slideIndex),
      },
    });
  };

  const setCountryToBeginning = (id: string) => {
    const numericLocationId = extractFromStringLocationId(id);

    historyPushState({
      id: RouteID.CountryProfiles,
      pathParams: {
        [countryProfilesCountryParamName]: numericLocationId.toString(),
        [countryProfilesSlideParamName]: slideIndexToSlug(0),
      },
    });
  };

  const numSlides = 12;
  const { index, next, prev, setIndex } = usePresentationStepperBehavior({
    numSlides,
    allowWrapAround: false,
    allowExternalIndexControl: true,
    externalIndex: slideIndex,
    setExternalIndex: setSlideIndex,
  });

  const goToConclusion = () => setIndex(numSlides - 1);
  const [tradeFlow, setTradeFlow] = useState<TradeFlow>(TradeFlow.Gross);
  const [treeMapColorCriterion, setTreeMapColorCriterion] = useState<
    TreeMapColorCriterion
  >(TreeMapColorCriterion.Complexity);
  const [
    newProductsTreeMapColorCriterion,
    setNewProductsTreeMapColorCriterion,
  ] = useState<TreeMapColorCriterion>(TreeMapColorCriterion.Sector);
  const [exportChangeYearRange, setExportChangeYearRange] = useState<
    LookBackYearRange
  >(LookBackYearRange.FiveYears);
  const [exportChangeNodeSizing, setExportChangeNodeSizing] = useState<
    ExportsChangeNodeSizing
  >(ExportsChangeNodeSizing.CountryTrade);
  const [productSpaceNodeSizing, setProductSpaceNodeSizing] = useState<
    ExportsChangeNodeSizing
  >(ExportsChangeNodeSizing.WorldTrade);
  const [policyDimension, setPolicyDimension] = useState<PolicyDimension>(
    PolicyDimension.BalancedPortfolio
  );
  const [helpTooltipsActive, setHelpTooltipsActive] = useState<boolean>(false);
  const [isSmallScreenMessageShown, setIsSmallScreenMessageShown] = useState<
    boolean
  >(false);
  const [isNotInCpMessageShown, setIsNotInCpMessageShown] = useState<boolean>(
    false
  );
  const [
    flowChartOpen,
    setFlowChartOpen,
  ] = useState<PolicyRecommendation | null>(null);

  const overlayPortalContainerNodeRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const node = document.querySelector<HTMLElement>(
      `#${overlayPortalContainerId}`
    );
    if (node !== null) {
      overlayPortalContainerNodeRef.current = node;
    }
  }, []);

  const dismissSmallScreenMessage = () => {
    setIsSmallScreenMessageShown(false);
    const { current: overlayPortalContainerEl } = overlayPortalContainerNodeRef;
    if (overlayPortalContainerEl !== null) {
      overlayPortalContainerEl.removeAttribute("style");
    }
  };

  const setSmallScreenMessage = () => {
    const mediaQueryList = window.matchMedia(smallScreenMediaQueryString);
    const { current: overlayPortalContainerEl } = overlayPortalContainerNodeRef;
    if (mediaQueryList.matches && overlayPortalContainerEl !== null) {
      overlayPortalContainerEl.style.position = "absolute";
      overlayPortalContainerEl.style.top = "0";
      overlayPortalContainerEl.style.right = "0";
      overlayPortalContainerEl.style.bottom = "0";
      overlayPortalContainerEl.style.left = "0";
      overlayPortalContainerEl.style.zIndex = "1000";
      setIsSmallScreenMessageShown(true);
    } else {
      dismissSmallScreenMessage();
    }
  };

  useEffect(() => {
    setSmallScreenMessage();
  }, []);

  useEffect(
    () => {
      const {
        current: overlayPortalContainerEl,
      } = overlayPortalContainerNodeRef;
      if (isNotInCpMessageShown && overlayPortalContainerEl !== null) {
        overlayPortalContainerEl.style.position = "absolute";
        overlayPortalContainerEl.style.top = "0";
        overlayPortalContainerEl.style.right = "0";
        overlayPortalContainerEl.style.bottom = "0";
        overlayPortalContainerEl.style.left = "0";
      }
    },
    [isNotInCpMessageShown]
  );

  const toggleHelpTooltips = () => {
    setHelpTooltipsActive(!helpTooltipsActive);
  };

  const [containerKey, setContainerKey] = useState<string>(
    `${locationId}-${window.innerWidth}-${window.innerHeight}`
  );
  useEffect(() => {
    let dpr = window.devicePixelRatio;
    const refreshOnResize = debounce(() => {
      if (window.devicePixelRatio === dpr) {
        setContainerKey(
          `${locationId}-${window.innerWidth}-${window.innerHeight}`
        );
        setSmallScreenMessage();
      }
      dpr = window.devicePixelRatio;
    }, 1000);
    window.addEventListener("resize", refreshOnResize);
    return () => {
      window.removeEventListener("resize", refreshOnResize);
    };
  }, []);

  const { localization, bundle } = useContext(
    CountryProfilesLocalizationAndBundleContext
  );
  const getFluentString: GetString = (id: string, args?: object) =>
    localization.getString(id, args);

  let content: React.ReactElement<any>;
  switch (index) {
    case 0:
      content = getLandingSlide({
        countryId: locationId,
        selectCountry: setCountry,
        getFluentString,
        setSlideIndex: setIndex,
        fluentBundle: bundle,
        backgroundZIndex: getZIndex("landingBackground"),
        contentZIndex: getZIndex("landingContent"),
        slideIndex: index,
        year: latestYear,
      });
      break;
    case 1:
      content = getExportBasketSlide({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        tradeFlow,
        setTradeFlow,
        tradeFlowDropdownZIndex: getTreeMapTradeFlowDropodwnZIndex(),
        restoreCountryProfilesZIndex: restoreZIndex,
        goToNextSlide: next,
        goToPrevSlide: prev,
        sectorLabelsZIndex,
        topOfPageIndicatorsZIndex: getZIndex("topPageIndicators"),
        hamburgerMenuZIndex,
        fluentBundle: bundle,
        sidebarZIndex,
        exportBasketExportComplexityTreeMapZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        exploreEngageButtonsZIndex,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
        promotedZIndex,
      });
      break;
    case 2:
      content = getExportComplexity({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        tradeFlow,
        setTradeFlow,
        colorCriterion: treeMapColorCriterion,
        setColorCriterion: setTreeMapColorCriterion,
        tradeFlowDropdownZIndex: getTreeMapTradeFlowDropodwnZIndex(),
        colorCriterionDropdownZIndex: getZIndex(
          CommonElementName.ExportBasketExportComplexityColorSelector
        ),
        hamburgerMenuZIndex,
        sidebarZIndex,
        legendZIndex: getZIndex("exportComplexityTreeMapLegend"),
        topOfPageIndicatorsZIndex: getZIndex("topPageIndicators"),
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        exportBasketExportComplexityTreeMapZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        exploreEngageButtonsZIndex,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
        promotedZIndex,
      });
      break;
    case 3:
      content = getExportGrowthDynamics({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        exportChangeYearRange,
        setExportChangeYearRange,
        exportChangeNodeSizing,
        setExportChangeNodeSizing,
        yearRangeDropdownZIndex: getZIndex(
          "exportGrowthDynamicsYearRangeSelector"
        ),
        nodeSizingDropdownZIndex: getZIndex("exportGrowthDynamicsSizeSelector"),
        promotedZIndex,
        goToNextSlide: next,
        goToPrevSlide: prev,
        exportGrowthDynamicsLegendZIndex: getZIndex(
          "exportComplexityTreeMapLegend"
        ),
        sectorLabelsZIndex,
        hamburgerMenuZIndex,
        sidebarZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        topOfPageDropShadowZIndex: getZIndex(
          "exportGrowthDynamicsTopOfPageDropShadow"
        ),
        chartZIndex: getZIndex("exportGrowthDynamicsGraph"),
        fluentBundle: bundle,
        exploreEngageButtonsZIndex,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 4:
      content = getGrowthGlobalMarketShare({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        sidebarZIndex,
        topOfPageIndicatorsZIndex: getZIndex("topPageIndicators"),
        hamburgerMenuZIndex,
        exploreEngageButtonsZIndex,
        chartZIndex: getZIndex("marketShareChart"),
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 5:
      content = getNewProducts({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        sidebarZIndex,
        hamburgerMenuZIndex,
        sectorLabelsZIndex,
        topOfPageIndicatorsZIndex: getZIndex("topPageIndicators"),
        chartZIndex: getZIndex("newProductsChart"),
        newProductsLegendZIndex: getZIndex("newProductsLegend"),
        tradeFlow,
        latestYear,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
        colorCriterion: newProductsTreeMapColorCriterion,
        setColorCriterion: setNewProductsTreeMapColorCriterion,
        colorCriterionDropdownZIndex: getZIndex(
          CommonElementName.ExportBasketExportComplexityColorSelector
        ),
        promotedZIndex,
      });
      break;

    case 6:
      content = getProductSpace({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        sidebarZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        chartZIndex: getZIndex("genericProductSpace"),
        sectionNavZIndex,
        hamburgerMenuZIndex,
        helpTipsTriggerZIndex,
        productSpaceLabelsZIndex: getZIndex("genericProductSpaceLabels"),
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 7:
      content = getCountryProductSpace({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        sidebarZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        chartZIndex: getZIndex("rockSongChart"),
        sectionNavZIndex,
        hamburgerMenuZIndex,
        topOfPageIndicatorsZIndex: getZIndex("topPageIndicators"),
        exploreEngageButtonsZIndex,
        productSpaceNodeSizing,
        setProductSpaceNodeSizing,
        nodeSizingDropdownZIndex: getZIndex("exportGrowthDynamicsSizeSelector"),
        promotedZIndex,
        sectorLabelsZIndex,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 8:
      content = getDiversificationPaths({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        sidebarZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        chartZIndex: getZIndex("rockSongChart"),
        sectionNavZIndex,
        hamburgerMenuZIndex,
        rockSongFlowChartZIndex: getZIndex("rockSongFlowChartZIndex"),
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
        flowChartOpen,
        setFlowChartOpen,
      });
      break;

    case 9:
      content = getGrowthOpportunities({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        policyDimension,
        setPolicyDimension,
        feasibilityTopPanelZIndex: getZIndex("feasibilyControls"),
        promoteFeasibilityTopPanelZIndex: () =>
          promoteZIndex("feasibilyControls"),
        restoreCountryProfilesZIndex: restoreZIndex,
        fluentBundle: bundle,
        sectorLabelsZIndex,
        chartZIndex: getZIndex("feasibilityChart"),
        sidebarZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        hamburgerMenuZIndex,
        goToConclusion,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 10:
      content = getHighPotentialProducts({
        getFluentString,
        countryId: locationId,
        year: latestYear,
        selectCountry: setCountry,
        slideIndex: index,
        setSlideIndex: setIndex,
        firstContentfulSlideIndex: 1,
        goToNextSlide: next,
        goToPrevSlide: prev,
        fluentBundle: bundle,
        sectorLabelsZIndex,
        countrySelectorZIndex: interiorCountrySelectorZIndex,
        sectionNavZIndex,
        sidebarZIndex,
        policyDimension,
        tableZIndex: getZIndex("topProductsTable"),
        hamburgerMenuZIndex,
        goToConclusion,
        helpTipsTriggerZIndex,
        toggleHelpTooltips,
        helpTooltipsActive,
      });
      break;

    case 11:
      content = getConclusionSlide({
        countryId: locationId,
        selectCountry: setCountryToBeginning,
        getFluentString,
        backgroundZIndex: getZIndex("conclusionBackground"),
        contentZIndex: getZIndex("conclusionContent"),
        slideIndex: index,
        year: latestYear,
        goToPrevSlide: prev,
      });
      break;

    default:
      throw new Error("Slide index " + index + " is out of bound");
  }

  let smallScreenMessage: React.ReactElement<any> | null;
  if (
    isSmallScreenMessageShown === true &&
    overlayPortalContainerNodeRef.current !== null
  ) {
    smallScreenMessage = ReactDOM.createPortal(
      <OverlayScreenMessageContainer>
        <div>
          <OverlayScreenLogo src={require("../img/Atlas2_logo_light.png")} />
        </div>
        <OverlayScreenMessage>
          {getFluentString("small-screen-notification-message", {
            width: smallScreenWidthThreshold.toString(),
            height: smallScreenHeightThreshold.toString(),
          })}
        </OverlayScreenMessage>
        <OverlayScreenMessageCloseButton
          dangerouslySetInnerHTML={{ __html: xIconHTMLEntity }}
          onClick={dismissSmallScreenMessage}
        />
      </OverlayScreenMessageContainer>,
      overlayPortalContainerNodeRef.current
    );
  } else {
    smallScreenMessage = null;
  }

  const locationIDNumber = extractFromStringLocationId(locationId);
  const newState: QueryStore = {
    country: locationIDNumber,
    queryLevel: QueryLevel.Location,
    product: undefined,
    year: latestYear,
    startYear: undefined,
    tradeDirection: TradeDirection.export,
    productClass: ProductClass.HS,
    nodeSizing: NodeSizing.None,
    tradeFlow: TradeFlow.Gross,
    target: Target.Product,
    partner: undefined,
  };
  const anchorProps = useLink({
    id: vizTypeToRouteID(VizType.Tree),
    queryParams: convertQueryStoreIntoQueryParams(VizType.Tree, newState),
  });

  const { loading, error, data } = useQuery<SuccessResponse, Variables>(query, {
    variables: { id: locationId },
  });
  let notInCpOverlay: React.ReactElement | null;
  if (loading === true) {
    notInCpOverlay = null;
  } else if (error !== undefined) {
    console.error(error);
    notInCpOverlay = null;
  } else if (data !== undefined) {
    const { location } = data;
    if (location === null) {
      notInCpOverlay = null;
    } else {
      if (
        location.isInCountryPages === false &&
        overlayPortalContainerNodeRef.current !== null &&
        !isNotInCpMessageShown
      ) {
        setIsNotInCpMessageShown(true);
        notInCpOverlay = ReactDOM.createPortal(
          <OverlayScreenMessageContainer style={{ opacity: 1 }}>
            <div>
              <OverlayScreenLogo
                src={require("../img/Atlas2_logo_light.png")}
              />
            </div>

            <Localized
              bundle={bundle}
              messageId="not-in-cp-notification-message"
              messageArgs={{
                "country-name": location.shortName,
                urlToExplore: (
                  <OverlayLink href={anchorProps.href}>
                    {getFluentString(
                      "not-in-cp-notification-message-explore-text"
                    )}
                  </OverlayLink>
                ),
              }}
            >
              <OverlayScreenMessage />
            </Localized>
          </OverlayScreenMessageContainer>,
          overlayPortalContainerNodeRef.current
        );
      } else {
        notInCpOverlay = null;
      }
    }
  }
  notInCpOverlay = null;

  return (
    <>
      {notInCpOverlay}
      <React.Fragment key={containerKey}>
        {content}
        {smallScreenMessage}
      </React.Fragment>
      <NewOverlayContainer
        zIndex={overlayZIndex}
        key={CommonElementName.OverlayContainer}
      />
    </>
  );
};

export default CountryProfiles;
